import { Components, Models, Utils } from '@sendum/core'
import React, { useCallback } from 'react'

import { ShipmentTimelineIcon } from './ShipmentTimelineIcon'
import s from './ShipmentTimelineSegment.module.scss'

interface IProps {
    segment?: Models.Segment
    type?: string
    name?: string
    icons?: Map<string, any>
}

export const ShipmentTimelineSegment = ({ segment, type, name, icons }: IProps): JSX.Element | null => {
    const getSegmentName = useCallback((): string => {
        if (!segment) {
            return ''
        }

        if (segment.segmentType === 'Route') {
            if (segment.flightNumber) {
                return ''.concat(Utils.checkStrValue(segment.routeCarrier), ' ', segment.flightNumber)
            }

            return Utils.checkStrValue(segment.routeCarrier)
        }

        return Utils.checkStrValue(segment.nodeName)
    }, [segment])

    const getSegmentAddress = (): string => {
        if (!segment) {
            return ''
        }

        if (segment.segmentType === 'Route') {
            return Utils.checkStrValue(segment.routeIdentifier)
        }

        return Utils.checkStrValue(segment.nodeAddress)
    }

    if (!segment) {
        return null
    }

    return (
        <div className={s.segment}>
            <div
                className={[
                    s.segment__vLine,
                    type === 'origin' ? s.segment__vLine_origin : '',
                    type === 'destination' ? s.segment__vLine_destination : '',
                    type === 'destination' && segment.segmentSequenceNo === 1 ? s.segment__vLine_singleNode : '',
                    segment.status === 'Completed' ? s.segment__vLine_completed : ''
                ].join(' ')}
            />
            {segment.status === 'InProgress' && (
                <div
                    className={[
                        s.segment__vLine_inProgress,
                        type === 'destination' && segment.segmentSequenceNo === 1
                            ? s.segment__vLine_inProgress_singleNode
                            : ''
                    ].join(' ')}
                />
            )}
            <div className={s.segment__column}>
                <div className={[s.segment__header, s.header].join(' ')}>
                    <div className={s.header__row}>
                        <div className={s.header__icon}>
                            <ShipmentTimelineIcon
                                mode={segment.routeMode || segment.nodeMode}
                                type={segment.segmentType}
                                icons={icons || Utils.defaultTimelineIcons}
                            />
                        </div>
                        <div className={s.header__type}>{name}</div>
                        <div className={[s.header__info, s.info].join(' ')}>
                            <div className={s.info__column}>
                                <div className={s.info__name}>{getSegmentName()}</div>
                                <div className={s.info__address}>{getSegmentAddress()}</div>
                                {segment.driverIdentifier && (
                                    <div className={s.info__identifier}>DriverId: {segment.driverIdentifier}</div>
                                )}
                                {segment.truckIdentifier && (
                                    <div className={s.info__identifier}>TruckId: {segment.truckIdentifier}</div>
                                )}
                                {segment.trailerIdentifier && (
                                    <div className={s.info__identifier}>TrailerId: {segment.trailerIdentifier}</div>
                                )}
                                {segment.segmentType === 'Route' && segment.jobIdentifier && (
                                    <div className={s.info__identifier}>JobId: {segment.jobIdentifier}</div>
                                )}
                                {segment.segmentType === 'Route' && segment.truckRegistrationNumber && (
                                    <div className={s.info__identifier}>TruckId: {segment.truckRegistrationNumber}</div>
                                )}
                                {segment.segmentType === 'Route' && segment.aircraftRegistrationNumber && (
                                    <div className={s.info__identifier}>
                                        Aircraft Registration #: {segment.aircraftRegistrationNumber}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {segment.milestonesForRendering &&
                    segment.milestonesForRendering.map((item, index) => (
                        <div
                            className={[s.segment__milestone, s.milestone].join(' ')}
                            data-key={`${index}${segment.segmentType}-${item.dateTime?.date || '--'}-${item.dateTime
                                ?.time || '--'}`}
                            key={`${index}${segment.segmentType}-${item.dateTime?.date || '--'}-${item.dateTime?.time ||
                                '--'}`}
                        >
                            <div className={s.milestone__column}>
                                {item.blueArrow && (
                                    <div className={[s.milestone__timeArrow, s.timeArrow].join(' ')}>
                                        <div className={s.timeArrow__row}>
                                            <div className={s.timeArrow__startDate}>{item.dateTime?.date || '--'}</div>
                                        </div>
                                    </div>
                                )}
                                <div className={[s.milestone__data, s.data].join(' ')}>
                                    {item.status === 'Completed' && (
                                        <div
                                            className={`${s.data__vLine} ${
                                                index === 0
                                                    ? s.data__firstvLine__withTimeArrow
                                                    : s.data__vLine__withTimeArrow
                                            }`}
                                        />
                                    )}
                                    <div className={s.data__row}>
                                        <div
                                            className={[
                                                s.data__line,
                                                item.status === 'Completed' ? s.data__line_completed : ''
                                            ].join(' ')}
                                        />
                                        <div className={s.data__dateTime}>
                                            {item.dateTime?.time || '--'} {item.dateTime?.tzAbbr || '--'}
                                        </div>
                                        <div className={s.data__info}>
                                            {item.timeLabel} {item.label}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}

                {type !== 'destination' && <Components.Divider height={1} color={'var(--sw-lightGray-color)'} />}
            </div>
        </div>
    )
}
